import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FaHeadset, FaComments, FaBook, FaVideo } from 'react-icons/fa';
import Form from './form';

const GradientHero = styled.div`
  background: linear-gradient(
    150deg,
    #ff80b5 0%,
    #9089fc 20%,
    #ff80b5 40%,
    #40c9ff 60%,
    #a5fecb 80%,
    #9089fc 100%
  );
  background-size: 200% 200%;
  animation: gradientMove 15s ease infinite;
  padding: 16rem 2rem 8rem 2rem;
  margin-top: -80px;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  @keyframes gradientMove {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 50% 50%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    filter: blur(60px);
    opacity: 0.6;
  }

  @media (max-width: 768px) {
    padding: 12rem 1rem 6rem 1rem;
    
    h1 {
      font-size: 2.5rem !important;
    }
    
    p {
      font-size: 1rem !important;
      padding: 0 1rem;
    }
  }
`;

const SupportGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-bottom: 5rem;
  padding: 4rem 2rem;

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 568px) {
    grid-template-columns: 1fr;
  }
`;

const SupportCard = styled(motion.div)`
  padding: 2rem;
  text-align: center;
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 16px;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.isDark ? 
      props.theme.colors.surface : 
      'white'
    };
    border-color: ${props => props.theme.colors.primary}40;
    transform: translateY(-5px);
    box-shadow: 0 4px 20px ${props => props.theme.isDark ? 
      'rgba(0, 0, 0, 0.3)' : 
      'rgba(79, 70, 229, 0.1)'
    };
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 16px;
  background: ${props => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  margin: 0 auto 1rem auto;
`;

const CardTitle = styled.h3`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const CardDescription = styled.p`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.6;
`;

const Contact = () => {
  const supportOptions = [
    {
      icon: <FaHeadset />,
      title: "24/7 Support",
      description: "Get help anytime via our dedicated support team",
      color: "#4f46e5"
    },
    {
      icon: <FaComments />,
      title: "Live Chat",
      description: "Connect instantly with our support specialists",
      color: "#7c3aed"
    },
    {
      icon: <FaBook />,
      title: "Documentation",
      description: "Detailed guides and API documentation",
      color: "#06b6d4"
    },
    {
      icon: <FaVideo />,
      title: "Video Tutorials",
      description: "Step-by-step visual learning resources",
      color: "#0ea5e9"
    }
  ];

  return (
    <>
      <GradientHero>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.h1
            style={{
              fontSize: '3.5rem',
              marginBottom: '1.5rem',
              fontWeight: 700,
              position: 'relative',
              zIndex: 2
            }}
          >
            Need Support?
          </motion.h1>
          <motion.p
            style={{
              fontSize: '1.25rem',
              maxWidth: '800px',
              margin: '0 auto',
              opacity: 0.9,
              lineHeight: 1.6,
              position: 'relative',
              zIndex: 2
            }}
          >
            We're here to help you succeed
          </motion.p>
        </motion.div>
      </GradientHero>

      <SupportGrid>
        {supportOptions.map((option, index) => (
          <SupportCard
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
          >
            <IconWrapper bgColor={option.color}>
              {option.icon}
            </IconWrapper>
            <CardTitle>{option.title}</CardTitle>
            <CardDescription>{option.description}</CardDescription>
          </SupportCard>
        ))}
      </SupportGrid>

      <Form />
    </>
  );
};

export default Contact;
